import type { Maybe } from '@adornis/base/utilTypes.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { subscribeParticipationsByInstitution } from '@adornis/buchungsheld-db/api/participation.js';
import { subscribeAllProductsByInstitution } from '@adornis/buchungsheld-db/api/product.js';
import { subscribeParticipantsByInstitutionPaginated } from '@adornis/buchungsheld-db/api/users.js';
import { BhExportDataView } from '@adornis/buchungsheld-db/BHExportDataView.js';
import { AdminInstitutionViewSetting } from '@adornis/buchungsheld-db/entities/AdminInstitutionViewSetting.js';
import { BHFilter } from '@adornis/buchungsheld-db/entities/bh-filter.js';
import { Participation } from '@adornis/buchungsheld-db/entities/Participation.js';
import { Product } from '@adornis/buchungsheld-db/entities/products.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { buildFullOptions, type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view.js';
import type { DataViewOperation } from '@adornis/data-view/data-view-operation.js';
import { exportDataView } from '@adornis/data-view/db/export-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import '@adornis/filter/client/x-filter-editor.js';
import { consume } from '@lit/context';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '../../components/bh-page-not-found.js';
import { entityContextTokenProvider } from '../../util.js';

const getLsKey = (institutionID: string, mode: string) => `extra_columns:${institutionID}:${mode}:admin-export:v1`;

function getCachedExtraColumns(institutionID: string, mode: string) {
  const lsKey = getLsKey(institutionID, mode);
  const extraColumns = JSON.parse(localStorage.getItem(lsKey) ?? '[]');
  return extraColumns;
}

function setCachedExtraColumns(institutionID: string, mode: string, columns: string[]) {
  const lsKey = getLsKey(institutionID, mode);
  localStorage.setItem(lsKey, JSON.stringify(columns));
}

/**
 * @element bh-page-admin-einrichtung-import-export
 */
@customElement('bh-page-admin-einrichtung-import-export')
export class BhPageAdminEinrichtungImportExport extends BuchungsheldLitElement {
  @property({ attribute: 'institution-id' }) institutionID = new RXController<string | null>(this, null);
  @property({ attribute: false }) selectedMode: typeof Participation | typeof User | typeof Product | null = null;

  @consume({ context: entityContextTokenProvider.getContext(AdminInstitutionViewSetting), subscribe: true })
  @state()
  protected readonly viewSettings?: { $instance: AdminInstitutionViewSetting };

  override render() {
    if (!this.institutionID.value) return html` <bh-page-not-found></bh-page-not-found> `;
    return html` <x-flex space="xl"> ${this._content()} </x-flex> `;
  }

  get colsDependingOnClass() {
    switch (this.selectedMode) {
      case Participation:
        return [
          'createdAt',
          'state',
          'updatedAt',
          'waitingListPlace',
          'product.hasPrice',
          'product.name',
          'product.capacity',
        ].map(fieldName => ({ fieldName }));
      case User:
        return ['createdAt', 'email', 'firstName', 'lastName'].map(fieldName => ({ fieldName }));
      case Product:
        return ['name', 'category.name', 'timeframe.name'].map(fieldName => ({ fieldName }));
      default:
        throw new Error('unreachable');
    }
  }

  private _content() {
    const institutionID = this.institutionID.value;
    if (!institutionID) return html` <tr-loading-state></tr-loading-state> `;

    if (!this.selectedMode)
      return html`
        <tr-h2> Was wollen Sie exportieren? </tr-h2>
        <x-grid
          columns="3"
          space="md"
          ${acss({
            'x-card': { width: '100%', textAlign: 'center' },
            'x-icon': { fontSize: '300%', color: this.colors.neutral.shade(300) },
          })}
        >
          <x-card crossaxis-center @click=${() => (this.selectedMode = Participation)}>
            <x-icon> diversity_3 </x-icon> <tr-h3> ${this._translation.translate('imp_exp', 'participations')} </tr-h3>
            <x-text> Einstiegspunkt für den Export sind die angemeldeten Nutzer auf den Produkten. </x-text>
          </x-card>
          <x-card crossaxis-center @click=${() => (this.selectedMode = User)}>
            <x-icon> person </x-icon> <tr-h3> ${this._translation.translate('imp_exp', 'users')} </tr-h3>
            <x-text> Export aller Nutzer der Institution. </x-text>
          </x-card>
          <x-card crossaxis-center @click=${() => (this.selectedMode = Product)}>
            <x-icon> package_2 </x-icon> <tr-h3> ${this._translation.translate('imp_exp', 'products')} </tr-h3>
            <x-text> Export aller Produkte der Institution. </x-text>
          </x-card>
        </x-grid>
      `;

    const extraColumns = getCachedExtraColumns(this.institutionID.value ?? '', this.selectedMode?._class) //
      .map(fieldName => ({ fieldName }));

    const operation = ((): DataViewOperation<any, any> => {
      switch (this.selectedMode) {
        case Participation:
          return subscribeParticipationsByInstitution;
        case User:
          return subscribeParticipantsByInstitutionPaginated;
        case Product:
          return subscribeAllProductsByInstitution;
        default:
          throw new Error('unreachable');
      }
    })();

    const shouldIncludeField = (baseQLFieldDefinition, path, baseEntity) => {
      const mode = this.selectedMode?._class;
      if (!mode) {
        console.warn('mode not set yet');
        return false;
      }

      // only show show members that are relevant for the given institution
      if (baseEntity._class === 'UserStammdaten') {
        const institutionStammdatenFields = UserStammdaten.getStammdatenFieldsForInstitution(institutionID);
        return institutionStammdatenFields.includes(baseQLFieldDefinition.name);
      }

      if (baseEntity._class === 'BHPayment') {
        const allowedFields = ['tax', 'status', 'products', 'amount', 'orderNr', 'discount'];
        return allowedFields.includes(baseQLFieldDefinition.name);
      }

      if (this.selectedMode?._class === 'Participation') {
        // stammdaten are already available through participation
        if (path.join('.') === 'user' && baseQLFieldDefinition.name === 'stammdaten') return false;
      }

      const whitelist = this.getFieldWhitelist(mode);
      return (
        whitelist.includes(path.join('.') + '.' + baseQLFieldDefinition.name) ||
        whitelist.includes(baseQLFieldDefinition.name)
      );
    };

    const { dataViewOptions, filterOptions } = buildFullOptions(
      {
        loader: () => html` <x-flex center crossaxis-center> <x-loader></x-loader> </x-flex> `,
        class: this.selectedMode,
        operation,
        defaultFieldMapping: {
          // string matches currently everything except DateTime
          // TODO: extra type for enums would be nice
          ['string' as any]: value => {
            const enumTranslation = UserStammdaten.getEnumTranslation(this._translation, value);
            if (enumTranslation) return enumTranslation;

            return undefined; // use dataview-default
          },
        },
        isColumnSelectable: true,
        isColumnResizable: true,
        fieldSelectionOptions: { shouldIncludeField },
        view: new View({
          filter: (() => {
            const filter = new BHFilter({
              institutionID,
              timeframeID: this.viewSettings?.$instance.selectedTimeframeID || undefined,
            });

            const savedFilter = this.getFilterForCurrentMode();
            const savedAspects = savedFilter?.aspects;
            const savedSortFields = savedFilter?.sortingFields;
            const savedSortDirections = savedFilter?.sortingDirections;

            if (savedAspects && savedAspects.length > 0) filter.aspects = savedAspects as any;
            if (savedSortFields && savedSortFields.length > 0) filter.sortingFields = savedSortFields as any;
            if (savedSortDirections && savedSortDirections.length > 0)
              filter.sortingDirections = savedSortDirections as any;

            return filter;
          })(),
        }),
        onViewChanged: view => {
          this.onViewUpdate(dataViewOptions, view);
          // TODO: save filter in onSortUpdate Event or somehow ignore dataview update
          // this.saveFilter(view.filter);
        },
        customColumns: [...this.colsDependingOnClass, ...extraColumns],
      },
      { fieldAspectGeneration: { shouldIncludeField } },
    );

    return html`
      <x-flex horizontal space-between crossaxis-center space="md">
        <x-tabs .selected=${[Participation, User, Product].indexOf(this.selectedMode)}>
          <x-tab icon="diversity_3" @click=${() => (this.selectedMode = Participation)}>
            ${this._translation.translate('imp_exp', 'participations')}
          </x-tab>
          <x-tab icon="person" @click=${() => (this.selectedMode = User)}>
            ${this._translation.translate('imp_exp', 'users')}
          </x-tab>
          <x-tab icon="package_2" @click=${() => (this.selectedMode = Product)}>
            ${this._translation.translate('imp_exp', 'products')}
          </x-tab>
        </x-tabs>
        <tr-button
          mode="outline"
          icon="cloud_download"
          @click=${() => exportDataView(dataViewOptions, { exportClass: BhExportDataView })}
        >
          ${this._translation.translate('imp_exp', 'export_everything')}
        </tr-button>
      </x-flex>
      <x-flex space="sm">
        <x-filter-editor
          .options=${filterOptions}
          @filter-apply=${e => this.saveFilter(e.detail)}
          @filter-reset=${() => this.saveFilter(null)}
        ></x-filter-editor>
        <x-data-view .options=${dataViewOptions}></x-data-view>
      </x-flex>
    `;
  }

  getFieldWhitelist(mode: string) {
    const productFields = [
      'name',
      'prices',
      'prices.unitAmount',
      'sortingWeight',
      'hasPrice',
      'category',
      'category.name',
      'institutionID',
      'timeframe',
      'timeframe.name',
      'productCopyID',
      'isLDAPOnly',
      'publishDate',
      'topic',
      'language',
      'presenceType',
      'isCanceled',
      'workshopSetName',
    ];

    const userFields = [
      'title',
      'firstName',
      'lastName',
      'email',
      'isLdapAccount',
      'whatsappNotification',
      'whatsappPhone',
      'receivesNewsletter',
    ];

    switch (mode) {
      case Participation._class:
        return [
          'product', //
          ...productFields.map(it => `product.${it}`),

          'user',
          ...userFields.map(it => `user.${it}`),

          'userStammdaten',

          'createdAt',
          'updatedAt',
          'currentPayment',
          'currentPayment.orderNr',
          'currentPayment.orderNr.year',
          'currentPayment.orderNr.nr',
          'currentPayment.discount',
          'currentPayment.createdAt',
          'currentPayment.tax',
          'currentPayment.amount',

          'state',
          'lastStateChangeWasInitiatedByAdmin',
          'isNoShow',
          'productPriceForUser',
          'fulfilledParticipationRequirements',
          'registrationDate',
          'signoffDate',
          'registrationDateThroughAdmin',
          'signoffDateThroughAdmin',
          'waitingListPlace',
          'nachrueckPriority',
          'nachrueckPossible',
        ];

      case User._class:
        return [...userFields, 'stammdaten'];

      case Product._class:
        return productFields;

      default:
        throw new Error('unreachable');
    }
  }

  getFilterForCurrentMode(): Maybe<BHFilter> {
    if (!this.viewSettings?.$instance) return null;
    const instance = this.viewSettings.$instance;

    switch (this.selectedMode) {
      case Participation:
        return instance.institutionDetailsExportParticipationsFilterV1;
      case User:
        return instance.institutionDetailsExportUsersFilterV1;
      case Product:
        return instance.institutionDetailsExportProductsFilterV1;
      default:
        return null;
    }
  }

  saveFilter = async (filter: any) => {
    if (!this.viewSettings?.$instance || !this.institutionID.value || !this.selectedMode) return;
    const instance = this.viewSettings.$instance;

    switch (this.selectedMode) {
      case Participation:
        instance.institutionDetailsExportParticipationsFilterV1 = filter;
        break;
      case User:
        instance.institutionDetailsExportUsersFilterV1 = filter;
        break;
      case Product:
        instance.institutionDetailsExportProductsFilterV1 = filter;
        break;
    }

    await instance.save();
  };

  onViewUpdate(dataViewOptions: DataViewOptions<any>, view: View<any>) {
    const institutionID = this.institutionID.value;
    const mode = this.selectedMode?._class;
    if (!institutionID || !mode) {
      console.warn('importanat data is not set yet. This Method should not have been called', { institutionID, mode });
      return;
    }

    const preExistingFields = dataViewOptions.customColumns!.map(col => col.fieldName).filter(isDefined);
    const cachedExtraFields = getCachedExtraColumns(institutionID, mode);
    const selectionFields = view.configuredColumns!.map(col => col.fieldName).filter(isDefined);
    const newFields = selectionFields.filter(it => !preExistingFields.includes(it));
    const fieldsToBeRemoved = preExistingFields.filter(it => !selectionFields.includes(it));
    const newCachedExtraFields = cachedExtraFields.filter(it => !fieldsToBeRemoved.includes(it));
    for (const field of newFields) newCachedExtraFields.push(field);

    console.log({ newFields, fieldsToBeRemoved });
    if (newFields.length || fieldsToBeRemoved.length) {
      setCachedExtraColumns(institutionID, mode, newCachedExtraFields);
      this.requestUpdate();
    }
  }
}
